import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { HexColorPicker } from 'react-colorful';

export const Colores = ({ objEmpresa, setObjEmpresa, coloresList, setColorThemeSelected }) => {
  const { colores } = useSelector(state => state.ui);

  const getInitialTheme = () => {
    const empresaSecondary = [
      objEmpresa.colorSecundario,
      objEmpresa.color3,
      objEmpresa.color4,
      objEmpresa.color5,
      objEmpresa.color6,
      objEmpresa.color7
    ];
    for (const theme of coloresList) {
      if (theme.main === objEmpresa.colorPrincipal &&
          empresaSecondary.every(color => theme.secondary.includes(color))) {
        return theme._id;
      }
    }
    const byMain = coloresList.find(t => t.main === objEmpresa.colorPrincipal);
    return byMain ? byMain._id : coloresList[0]._id;
  };

  const [themeSelected, setThemeSelected] = useState(getInitialTheme);

  const [customColors, setCustomColors] = useState({
    main: objEmpresa.colorPrincipal || '#1A28D7',
    secondary: [
      objEmpresa.colorSecundario || '#D6E8FF',
      objEmpresa.color3 || '#FDFFAE',
      objEmpresa.color4 || '#D2FAFF',
      objEmpresa.color5 || '#F67F8C',
      objEmpresa.color6 || '#F8FBFF',
      objEmpresa.color7 || '#F4EFEF'
    ]
  });

  const [activePickerIndex, setActivePickerIndex] = useState(null);
  const popoverRef = useRef(null);
  const circleRefs = useRef([]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        activePickerIndex !== null &&
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        !circleRefs.current.some(ref => ref && ref.contains(e.target))
      ) {
        setActivePickerIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activePickerIndex]);

  const selectTheme = (id) => {
    const theme = coloresList.find(t => t._id === id);
    if (!theme) return;

    setCustomColors({
      main: theme.main,
      secondary: [...theme.secondary]
    });

    setObjEmpresa({
      ...objEmpresa,
      colorPrincipal: theme.main,
      colorSecundario: theme.secondary[0],
      color3: theme.secondary[1],
      color4: theme.secondary[2],
      color5: theme.secondary[3],
      color6: theme.secondary[4],
      color7: theme.secondary[5],
    });
    setColorThemeSelected(theme);
    setThemeSelected(id);
    setActivePickerIndex(null);
  };

  const handleColorChange = useCallback((color) => {
    if (activePickerIndex === null) return;

    setCustomColors(prev => {
      const updated = { ...prev };
      if (activePickerIndex === 0) {
        updated.main = color;
      } else {
        updated.secondary = [...prev.secondary];
        updated.secondary[activePickerIndex - 1] = color;
      }

      const newEmpresa = {
        colorPrincipal: updated.main,
        colorSecundario: updated.secondary[0],
        color3: updated.secondary[1],
        color4: updated.secondary[2],
        color5: updated.secondary[3],
        color6: updated.secondary[4],
        color7: updated.secondary[5],
      };
      setObjEmpresa(prev => ({ ...prev, ...newEmpresa }));
      setColorThemeSelected({
        _id: 'custom',
        name: 'custom',
        main: updated.main,
        secondary: [...updated.secondary]
      });

      return updated;
    });
  }, [activePickerIndex, setObjEmpresa, setColorThemeSelected]);

  const handleCircleClick = (index, e) => {
    e.stopPropagation();
    setActivePickerIndex(prev => prev === index ? null : index);
  };

  const getColorForIndex = (index) => {
    if (index === 0) return customColors.main;
    return customColors.secondary[index - 1];
  };

  const handleHexInput = (e) => {
    let val = e.target.value.replace(/[^0-9a-fA-F#]/g, '');
    if (!val.startsWith('#')) val = '#' + val;
    if (val.length <= 7) {
      handleColorChange(val);
    }
  };

  const colorLabels = ['Principal', 'Color 2', 'Color 3', 'Color 4', 'Color 5', 'Color 6', 'Color 7'];

  const isModified = () => {
    const theme = coloresList.find(t => t._id === themeSelected);
    if (!theme) return false;
    if (customColors.main !== theme.main) return true;
    return customColors.secondary.some((c, i) => c !== theme.secondary[i]);
  };

  const resetToTheme = (e) => {
    e.stopPropagation();
    selectTheme(themeSelected);
  };

  return (
    <div className='panel-container color-container'>
      {
        coloresList.map(colorItem => {
          const isSelected = colorItem._id === themeSelected;
          const showReset = isSelected && isModified();
          return (
            <div
              key={colorItem._id}
              className='color-selector'
              onClick={() => selectTheme(colorItem._id)}
            >
              <div className={`ylm-radio ${isSelected && 'active'}`}>
                <div
                  className='radio-bg'
                  style={{ backgroundColor: colores.color6 }}
                >
                  <span
                    className='radio-item'
                    style={{ backgroundColor: colores.colorPrincipal }}
                  ></span>
                </div>
              </div>
              <div className='color-theme'>
                {isSelected ? (
                  <>
                    <div
                      className='main-color color color-editable editable-active'
                      style={{ backgroundColor: customColors.main }}
                      onClick={(e) => handleCircleClick(0, e)}
                      ref={el => circleRefs.current[0] = el}
                      title={colorLabels[0]}
                    >
                      <span className='edit-icon'>&#9998;</span>
                    </div>
                    <div className='secondary-colors'>
                      {customColors.secondary.map((color, idx) => (
                        <div
                          key={`editable-${idx}`}
                          className='color color-editable editable-active'
                          style={{ backgroundColor: color }}
                          onClick={(e) => handleCircleClick(idx + 1, e)}
                          ref={el => circleRefs.current[idx + 1] = el}
                          title={colorLabels[idx + 1]}
                        >
                          <span className='edit-icon'>&#9998;</span>
                        </div>
                      ))}
                    </div>
                    {showReset && (
                      <button className='reset-theme-btn' onClick={resetToTheme} title='Restaurar colores originales'>
                        &#8634;
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <div className='main-color color' style={{ backgroundColor: colorItem.main }}></div>
                    <div className='secondary-colors'>
                      {colorItem.secondary.map((second, idx) => (
                        <div key={`secondary${idx}`} className='color' style={{ backgroundColor: second }}></div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })
      }

      {activePickerIndex !== null && (
        <div className='color-picker-popover' ref={popoverRef}>
          <div className='picker-header'>
            <span className='picker-label'>{colorLabels[activePickerIndex]}</span>
            <button
              className='picker-close'
              onClick={() => setActivePickerIndex(null)}
            >
              &times;
            </button>
          </div>
          <HexColorPicker
            color={getColorForIndex(activePickerIndex)}
            onChange={handleColorChange}
          />
          <div className='picker-input-row'>
            <span className='hash-prefix'>#</span>
            <input
              className='hex-input'
              type='text'
              value={getColorForIndex(activePickerIndex).replace('#', '')}
              onChange={handleHexInput}
              maxLength={6}
              spellCheck={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}
